import React, { useEffect, useMemo, useRef, useState } from 'react';
import { WebSocketContext } from './WebSocketContext';
import { retrieveCachedUserDetails } from '../thunks/session_thunks';
import { getBackendUri } from '../utils/EnvConfigUtils';

const WS_URL = 'wss://ws.useopenwrench.com';

const PING_INTERVAL = 30000;
const RECONNECT_INTERVAL = 3000;

export const WebSocketProvider = ({ children }) => {
	const socketRef = useRef(null);
	const reconnectTimerRef = useRef(null);
	const pingIntervalRef = useRef(null);

	const [isConnected, setIsConnected] = useState(false);
	const [socket, setSocket] = useState(null);

	const BACKEND_URI = getBackendUri();

	const enabled =
		BACKEND_URI === 'https://api.useopenwrench.com' ||
		process.env.REACT_APP_ENABLE_WEBSOCKET === 'true';

	// Track auth in state so React can react to it
	const [auth, setAuth] = useState(() => retrieveCachedUserDetails(['token', 'userType']));

	// If login stores token in localStorage, this catches cross-tab updates.
	// Same-tab updates typically won't fire 'storage', so we also poll lightly.
	useEffect(() => {
		if (!enabled) return;

		const readAuth = () => {
			const next = retrieveCachedUserDetails(['token', 'userType']);
			setAuth((prev) => {
				if (prev.token === next.token && prev.userType === next.userType) return prev;
				return next;
			});
		};

		readAuth();

		const onStorage = (e) => {
			if (e.key && !e.key.toLowerCase().includes('token')) return;
			readAuth();
		};

		window.addEventListener('storage', onStorage);

		// Same-tab: poll until token appears (stops once token is present)
		const poll = setInterval(() => {
			readAuth();
		}, 500);

		return () => {
			window.removeEventListener('storage', onStorage);
			clearInterval(poll);
		};
	}, [enabled]);

	const cleanupTimers = () => {
		if (reconnectTimerRef.current) {
			clearTimeout(reconnectTimerRef.current);
			reconnectTimerRef.current = null;
		}
		if (pingIntervalRef.current) {
			clearInterval(pingIntervalRef.current);
			pingIntervalRef.current = null;
		}
	};

	const connect = () => {
		const cachedUserDetails = retrieveCachedUserDetails(['token', 'userType']);
		const token = cachedUserDetails.token;
		const userType = cachedUserDetails.userType;

		if (!enabled || !token || !userType) return;

		// Avoid duplicate connections
		if (
			socketRef.current &&
			(socketRef.current.readyState === WebSocket.OPEN ||
				socketRef.current.readyState === WebSocket.CONNECTING)
		) {
			return;
		}

		cleanupTimers();

		const ws = new WebSocket(
			`${WS_URL}/${userType}/connect?token=${encodeURIComponent(
				token
			)}&validateTokenAgainst=${encodeURIComponent(getBackendUri())}`
		);
		socketRef.current = ws;
		setSocket(ws); // force React to propagate the new socket

		ws.onopen = () => {
			setIsConnected(true);

			if (reconnectTimerRef.current) {
				clearTimeout(reconnectTimerRef.current);
				reconnectTimerRef.current = null;
			}

			pingIntervalRef.current = setInterval(() => {
				if (ws.readyState === WebSocket.OPEN) {
					ws.send('ping');
				}
			}, PING_INTERVAL);
		};

		ws.onclose = (err) => {
			console.log('WS error');
			console.log(err);
			setIsConnected(false);

			if (pingIntervalRef.current) {
				clearInterval(pingIntervalRef.current);
				pingIntervalRef.current = null;
			}

			if (enabled) {
				reconnectTimerRef.current = setTimeout(() => {
					console.log('REconnecting....');
					connect();
				}, RECONNECT_INTERVAL);
			}
		};

		ws.onerror = (err) => {
			ws.close();
		};
	};

	useEffect(() => {
		if (!enabled) {
			if (socketRef.current) socketRef.current.close();
			if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
			if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);

			setIsConnected(false);
			setSocket(null);
			console.log("Won't connect to Websocket for this user...");
			return;
		}

		console.log('WS connection initiating');

		connect();

		return () => {
			if (socketRef.current) socketRef.current.close();
			if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
			if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
		};
	}, [enabled, auth]);

	const sendMessage = (msg) => {
		if (socketRef.current && isConnected) {
			socketRef.current.send(JSON.stringify(msg));
		}
	};

	const value = useMemo(
		() => ({
			socket: socketRef.current,
			isConnected,
			sendMessage,
		}),
		[socket, isConnected]
	);

	return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
};
